import React from 'react';
import { UserPlus } from 'lucide-react';

const AddContactModal = ({
  isOpen,
  onClose,
  // Form fields
  newContactName,
  setNewContactName,
  newContactEmail,
  setNewContactEmail,
  newContactPhone,
  setNewContactPhone,
  newContactCompany,
  setNewContactCompany,
  newContactRole,
  setNewContactRole,
  newContactCategory,
  setNewContactCategory,
  newContactNotes,
  setNewContactNotes,
  // Auto scan
  contactAutoScanning,
  onAutoScan,
  // Save handler
  onSave,
  // Loading state
  saving
}) => {
  if (!isOpen) return null;

  const handleCancel = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={handleCancel}>
      <div className="bg-gray-900 border border-gray-400/20 rounded-2xl w-[600px] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="p-6 border-b border-gray-400/20">
          <h2 className="text-2xl font-bold text-white">Add Contact</h2>
          <p className="text-gray-400 text-sm mt-1">Create a new contact from this conversation</p>
        </div>

        {/* Contact Form */}
        <div className="p-6 space-y-4 max-h-[500px] overflow-y-auto">
          {/* Name Field */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
            <input
              type="text"
              value={newContactName}
              onChange={(e) => setNewContactName(e.target.value)}
              placeholder="Enter contact name"
              className="w-full px-4 py-2 bg-gray-800 border border-gray-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400"
              data-testid="contact-name-input"
            />
          </div>

          {/* Email Field */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
            <input
              type="email"
              value={newContactEmail}
              onChange={(e) => setNewContactEmail(e.target.value)}
              placeholder="email@example.com"
              className="w-full px-4 py-2 bg-gray-800 border border-gray-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400"
              data-testid="contact-email-input"
            />
          </div>

          {/* Phone Field with Auto Scan */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
            <div className="flex gap-2">
              <input
                type="tel"
                value={newContactPhone}
                onChange={(e) => setNewContactPhone(e.target.value)}
                placeholder="+1234567890"
                className="flex-1 px-4 py-2 bg-gray-800 border border-gray-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400"
                data-testid="contact-phone-input"
              />
              <button
                onClick={onAutoScan}
                disabled={contactAutoScanning}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="contact-auto-scan-btn"
              >
                {contactAutoScanning ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Scanning...
                  </>
                ) : (
                  'Auto Scan'
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Enter phone number, then click &apos;Auto Scan&apos; to find match from iCloud contacts</p>
          </div>

          {/* Company Field */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Company</label>
            <input
              type="text"
              value={newContactCompany}
              onChange={(e) => setNewContactCompany(e.target.value)}
              placeholder="Company name"
              className="w-full px-4 py-2 bg-gray-800 border border-gray-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400"
              data-testid="contact-company-input"
            />
          </div>

          {/* Role Field */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Role</label>
            <input
              type="text"
              value={newContactRole}
              onChange={(e) => setNewContactRole(e.target.value)}
              placeholder="Job title or role"
              className="w-full px-4 py-2 bg-gray-800 border border-gray-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400"
              data-testid="contact-role-input"
            />
          </div>

          {/* Category Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
            <select
              value={newContactCategory}
              onChange={(e) => setNewContactCategory(e.target.value)}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-400/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
              data-testid="contact-category-select"
            >
              <option value="Personal">Personal</option>
              <option value="Business">Business</option>
              <option value="Family">Family</option>
              <option value="Friends">Friends</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Notes Field */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Notes</label>
            <textarea
              value={newContactNotes}
              onChange={(e) => setNewContactNotes(e.target.value)}
              rows="3"
              placeholder="Additional notes..."
              className="w-full px-4 py-2 bg-gray-800 border border-gray-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
              data-testid="contact-notes-input"
            ></textarea>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="p-6 border-t border-gray-400/20 flex gap-3">
          <button
            onClick={handleCancel}
            className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors text-white font-medium"
            data-testid="contact-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 rounded-lg transition-colors text-white font-medium flex items-center justify-center gap-2"
            data-testid="contact-save-btn"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Saving...
              </>
            ) : (
              <>
                <UserPlus size={18} />
                Save Contact
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddContactModal;
